import { X } from 'lucide-react';
import { type ComponentType, useEffect, useMemo, useRef, useState } from 'react';
import { type Control, useController } from 'react-hook-form';
import type { GroupBase, SingleValue, StylesConfig } from 'react-select';
import type { CreatableProps } from 'react-select/creatable';
import CreatableSelect from 'react-select/creatable';

import { DEFAULT_GRANT_ROLE, GRANT_ROLES, MAX_USER_GRANTS } from '../../constants/users.js';
import { useCreateTag, useIntl, useTagAutocomplete, useTagDeviceCounts } from '../../hooks';
import { cn } from '../../lib/utils';
import type { Tag } from '../../types/tag.js';
import type { GrantRole, UserGrant } from '../../types/user.js';
import type { UserFormValues } from '../../validations/user-form.js';
import { Badge } from '../ui';

type UserGrantsFieldProps = {
  control: Control<UserFormValues>;
  disabled?: boolean;
};

interface TagOption {
  value: string;
  label: string;
  tagId?: string;
}

type CreatableSelectProps = CreatableProps<TagOption, false, GroupBase<TagOption>>;

const CreatableSelectComponent = CreatableSelect as unknown as ComponentType<CreatableSelectProps>;

const GRANT_ROW_GRID = 'grid grid-cols-[minmax(0,1fr)_9rem_1.5rem] items-center gap-3 px-3';

const selectStyles: StylesConfig<TagOption, false, GroupBase<TagOption>> = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 40,
    borderRadius: 6,
    borderColor: 'var(--color-border)',
    color: 'var(--color-foreground)',
    ':hover': {
      borderColor: 'var(--color-border)',
      cursor: 'pointer',
    },
    backgroundColor: state.isDisabled ? 'var(--color-background-dark)' : 'var(--color-input)',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 30,
    backgroundColor: 'var(--color-background-light)',
    border: '1px solid var(--color-border)',
  }),
  option: (provided) => ({
    ...provided,
    backgroundColor: 'var(--color-background-light)',
    color: 'var(--color-foreground)',
    ':hover': {
      backgroundColor: 'var(--color-background-dark)',
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: 'var(--color-foreground)',
  }),
};

export function UserGrantsField({ control, disabled = false }: UserGrantsFieldProps) {
  const intl = useIntl();
  const {
    field: { value: formValue = [], onChange },
  } = useController({ name: 'grants', control });

  const grants: UserGrant[] = useMemo(
    () => (Array.isArray(formValue) ? formValue : []),
    [formValue],
  );
  // A tag created over the network lands after the operator may have edited other rows.
  const latestGrantsRef = useRef(grants);
  useEffect(() => {
    latestGrantsRef.current = grants;
  }, [grants]);
  const [inputValue, setInputValue] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  const assignedNames = useMemo(() => {
    return new Set(grants.map((grant) => grant.tagName.toLowerCase()));
  }, [grants]);

  const suggestionsQuery = useTagAutocomplete(inputValue.trim(), 10, true);

  const availableOptions: TagOption[] = useMemo(() => {
    const data = suggestionsQuery.data ?? [];
    return data
      .filter((tag) => !assignedNames.has(tag.name.toLowerCase()))
      .map((tag) => ({ value: tag.name, label: tag.name, tagId: tag.id }));
  }, [assignedNames, suggestionsQuery.data]);

  const maxReached = grants.length >= MAX_USER_GRANTS;

  const deviceCountsQuery = useTagDeviceCounts(grants.map((grant) => grant.tagId));
  const deviceCountByTagId = useMemo(
    () =>
      new Map(deviceCountsQuery.data?.tags.map(({ tagId, deviceCount }) => [tagId, deviceCount])),
    [deviceCountsQuery.data],
  );
  const totalDevices =
    deviceCountsQuery.data && !deviceCountsQuery.isPlaceholderData
      ? deviceCountsQuery.data.totalDevices
      : null;

  const createTag = useCreateTag();

  const resetInput = () => {
    setInputValue('');
  };

  const handleInputChange: NonNullable<CreatableSelectProps['onInputChange']> = (
    nextValue,
    action,
  ) => {
    if (action.action === 'input-change') {
      setInputValue(nextValue);
    } else if (action.action === 'menu-close') {
      resetInput();
    }
  };

  const handleAddTag = (tag: Tag) => {
    const latestGrants = latestGrantsRef.current;
    if (latestGrants.some((grant) => grant.tagId === tag.id)) {
      setMessage(intl.formatMessage({ id: 'userGrants.alreadyAssigned' }, { name: tag.name }));
      resetInput();
      return;
    }
    onChange([...latestGrants, { tagId: tag.id, tagName: tag.name, role: DEFAULT_GRANT_ROLE }]);
    setMessage(null);
    resetInput();
  };

  const handleSelect = (option: SingleValue<TagOption>) => {
    if (!option) return;
    if (option.tagId) {
      const suggestion = suggestionsQuery.data?.find((tag) => tag.id === option.tagId);
      if (suggestion) {
        handleAddTag(suggestion);
      } else {
        handleAddTag({ id: option.tagId, name: option.label });
      }
      return;
    }
    const normalized = option.value.toLowerCase();
    const suggestion = suggestionsQuery.data?.find((tag) => tag.name.toLowerCase() === normalized);
    if (suggestion) {
      handleAddTag(suggestion);
    }
  };

  const handleCreate = async (value: string) => {
    const name = value.trim();
    if (!name) return;
    if (maxReached) {
      setMessage(intl.formatMessage({ id: 'userGrants.maxReached' }, { max: MAX_USER_GRANTS }));
      resetInput();
      return;
    }
    if (name.length > 64) {
      setMessage(intl.formatMessage({ id: 'userGrants.maxLength' }));
      resetInput();
      return;
    }
    const normalized = name.toLowerCase();
    if (assignedNames.has(normalized)) {
      setMessage(intl.formatMessage({ id: 'userGrants.alreadyAssigned' }, { name }));
      resetInput();
      return;
    }
    const suggestion = suggestionsQuery.data?.find((tag) => tag.name.toLowerCase() === normalized);
    if (suggestion) {
      handleAddTag(suggestion);
      return;
    }
    try {
      const created = await createTag.mutateAsync({ name });
      handleAddTag(created);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : intl.formatMessage({ id: 'userGrants.createError' });
      setMessage(msg);
    }
  };

  const handleRemove = (tagId: string) => {
    onChange(grants.filter((grant) => grant.tagId !== tagId));
    setMessage(null);
  };

  const handleRoleChange = (tagId: string, role: GrantRole) => {
    onChange(grants.map((grant) => (grant.tagId === tagId ? { ...grant, role } : grant)));
  };

  return (
    <div className="space-y-2" role="group" aria-labelledby="user-grants-heading">
      <div className="flex items-baseline justify-between gap-3">
        <span id="user-grants-heading" className="text-sm font-medium leading-none">
          {intl.formatMessage({ id: 'userGrants.label' })}
        </span>
        {grants.length > 0 ? (
          <span className="text-xs text-muted-foreground">
            {totalDevices === null
              ? intl.formatMessage({ id: 'userGrants.summaryTagsOnly' }, { tags: grants.length })
              : intl.formatMessage(
                  { id: 'userGrants.summary' },
                  { tags: grants.length, devices: totalDevices },
                )}
          </span>
        ) : null}
      </div>

      {/* No overflow clipping: the tag picker's menu opens below the last row and must stay visible. */}
      <div className="rounded-md border border-border bg-card text-sm">
        <div
          className={cn(
            GRANT_ROW_GRID,
            'rounded-t-md bg-muted py-2 text-xs font-semibold text-muted-foreground',
          )}
        >
          <span>{intl.formatMessage({ id: 'userGrants.columnTag' })}</span>
          <span>{intl.formatMessage({ id: 'userGrants.columnRole' })}</span>
          <span aria-hidden="true" />
        </div>

        {grants.length === 0 ? (
          <div className="px-3 py-3 text-sm text-muted-foreground">
            {intl.formatMessage({ id: 'userGrants.noGrants' })}
          </div>
        ) : (
          <ul className="divide-y divide-border" data-testid="user-grants-list">
            {grants.map((grant) => {
              const deviceCount = deviceCountByTagId.get(grant.tagId);
              return (
                <li
                  key={grant.tagId}
                  className={cn(GRANT_ROW_GRID, 'py-2')}
                  data-testid="user-grant-row"
                  data-tag-name={grant.tagName}
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <Badge variant="secondary" className="min-w-0 max-w-full text-xs">
                      <span className="truncate" title={grant.tagName}>
                        {grant.tagName}
                      </span>
                    </Badge>
                    {deviceCount === undefined ? null : (
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {intl.formatMessage(
                          { id: 'userGrants.deviceCount' },
                          { count: deviceCount },
                        )}
                      </span>
                    )}
                  </div>
                  <select
                    value={grant.role}
                    onChange={(event) =>
                      handleRoleChange(grant.tagId, event.target.value as GrantRole)
                    }
                    disabled={disabled}
                    aria-label={intl.formatMessage(
                      { id: 'userGrants.roleFor' },
                      { name: grant.tagName },
                    )}
                    data-testid="user-grant-role-select"
                    className="h-8 w-full rounded-md border border-border bg-input px-2 text-sm shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                  >
                    {GRANT_ROLES.map((role) => (
                      <option key={role} value={role}>
                        {intl.formatMessage({ id: `userGrants.role.${role}` })}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="inline-flex h-6 w-6 items-center justify-center rounded-full transition-colors hover:bg-background-dark"
                    aria-label={intl.formatMessage(
                      { id: 'userGrants.removeTag' },
                      { name: grant.tagName },
                    )}
                    onClick={() => handleRemove(grant.tagId)}
                    disabled={disabled}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className={cn(GRANT_ROW_GRID, 'border-t border-dashed border-border py-2')}>
          <CreatableSelectComponent
            inputValue={inputValue}
            onInputChange={handleInputChange}
            value={null}
            onChange={handleSelect}
            onCreateOption={(value) => {
              void handleCreate(value);
            }}
            isClearable
            classNamePrefix="user-tags-select"
            // Inside the scrolling dialog: scroll the menu into view or flip it up. A menu portalled
            // to body would sit outside the Radix modal layer.
            menuPlacement="auto"
            placeholder={intl.formatMessage({ id: 'userGrants.placeholder' })}
            isDisabled={disabled || maxReached || createTag.isPending}
            isLoading={suggestionsQuery.isFetching || createTag.isPending}
            options={availableOptions}
            formatCreateLabel={(value: string) =>
              intl.formatMessage({ id: 'userGrants.createLabel' }, { value })
            }
            components={{ IndicatorSeparator: null }}
            styles={selectStyles}
            inputId="user-tags-select"
            instanceId="user-tags-select"
          />
          <span className="col-span-2 text-xs text-muted-foreground">
            {intl.formatMessage({ id: 'userGrants.defaultRoleHint' })}
          </span>
        </div>
      </div>

      {message ? <div className="text-sm text-destructive">{message}</div> : null}
      {maxReached ? (
        <div className="text-xs text-muted-foreground">
          {intl.formatMessage({ id: 'userGrants.maxReached' }, { max: MAX_USER_GRANTS })}
        </div>
      ) : null}
      <p className="text-xs text-muted-foreground">
        {intl.formatMessage({ id: 'userGrants.footnote' })}
      </p>
    </div>
  );
}
